import { MultiAutocomplete } from '@common/ui'
import { com } from '@eidu/web'
import { useMemo } from 'react'
import { PermissionPolicyDTO } from '../../domain/permission/PermissionPolicyDTO'
import EqualityHashMap from '../../util/EqualityHashMap'
import PermissionPolicyId = com.eidu.entity.permission.PermissionPolicyId

type PolicyMultiSelectProps = {
  loadedPolicies: ReadonlyArray<PermissionPolicyDTO>
  selectedPolicies: ReadonlyArray<PermissionPolicyDTO>
  selectedPolicyIds: ReadonlyArray<PermissionPolicyId>
  onChange: (policyIds: ReadonlyArray<PermissionPolicyId>) => void
  hasMore: boolean
  loadMore: () => void
  loading?: boolean
}

const PolicyMultiSelect = ({
  loadedPolicies,
  selectedPolicies,
  selectedPolicyIds,
  onChange,
  hasMore,
  loadMore,
  loading,
}: PolicyMultiSelectProps) => {
  const optionsById = useMemo(() => {
    const map = new EqualityHashMap<PermissionPolicyId, PermissionPolicyDTO>()
    selectedPolicies.forEach((p) => map.set(p.policyId, p))
    loadedPolicies.forEach((p) => map.set(p.policyId, p))
    return map
  }, [loadedPolicies, selectedPolicies])

  const selectedIdSet = useMemo(() => {
    const map = new EqualityHashMap<PermissionPolicyId, true>()
    selectedPolicyIds.forEach((id) => map.set(id, true))
    return map
  }, [selectedPolicyIds])

  const idsByString = useMemo(() => new Map([...optionsById.keys()].map((id) => [id.toString(), id])), [optionsById])

  const sortedOptions = useMemo(
    () =>
      [...optionsById.values()]
        .filter((p) => !selectedIdSet.get(p.policyId))
        .sort((a, b) => a.policy.name.localeCompare(b.policy.name)),
    [optionsById, selectedIdSet]
  )

  const data = sortedOptions.map((p) => ({ value: p.policyId.toString(), label: p.policy.name }))
  const value = selectedPolicyIds.flatMap((id) => (optionsById.get(id) ? [id.toString()] : []))

  return (
    <div style={{ paddingInline: 16, paddingTop: 12, paddingBottom: 16 }}>
      <MultiAutocomplete
        size="sm"
        data={data}
        value={value}
        loading={loading}
        loadMore={loadMore}
        hasMore={hasMore}
        label="Add policy"
        placeholder="Search policies to add"
        styles={{ pill: { display: 'none' } }}
        onChange={(next) => {
          const additions = next
            .map((v) => idsByString.get(v))
            .filter((id): id is PermissionPolicyId => !!id && !selectedIdSet.get(id))
          if (additions.length === 0) return
          onChange([...selectedPolicyIds, ...additions])
        }}
      />
    </div>
  )
}

export default PolicyMultiSelect
